import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { invalidateMeetingPrepSettings, MEETING_PREP_SETTINGS_QUERY_KEY } from './useMeetingPrepSettings';

/** Fields a user can edit; `id`/`accountId`/`creatorId`/`lastRunDate` are server-managed. */
export type MeetingPrepSettingsUpdate = Pick<
	Api.IMeetingPrepUserSettings,
	'enabled' | 'sendEmail' | 'sendHourLocal' | 'weekendDelivery' | 'includeInternalAttendees' | 'defaultPrompt'
>;

/**
 * Upserts the calling user's Meeting Prep preferences (PUT /MeetingPrep/preferences). Keyed by
 * (accountId, userId) server-side — the authenticated user always wins, so no user id is sent.
 */
export function useMeetingPrepSettingsMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (settings: Api.IMeetingPrepUserSettings) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: (settings: MeetingPrepSettingsUpdate) => {
			return userSession.webServiceHelper.callAsync<Api.IMeetingPrepUserSettings>(
				'MeetingPrep/preferences',
				'PUT',
				settings
			);
		},
		onError,
		onSuccess: settings => {
			// Optimistically reflect the persisted record for immediate UI feedback...
			reactQueryClient.setQueryData(MEETING_PREP_SETTINGS_QUERY_KEY, settings);
			onSuccess?.(settings);
		},
		// ...then reconcile with the server on settle. This refetches authoritative, server-normalized
		// fields (lastRunDate, defaults) and ensures a stale/raced or failed PUT can't leave the cache
		// out of sync with the backend.
		onSettled: () => {
			invalidateMeetingPrepSettings();
		},
	});
}
